"use strict";
/**
 * Capability accessors.
 *
 * Shared code asks these instead of comparing venue names. `dataSource ===
 * "kalshi"` is an identity check standing in for a capability question, and it
 * has to be revisited for every venue added; these do not.
 *
 * **They never throw.** `getVenue` throws on an unregistered venue, which is
 * right at an order path and wrong here — several call sites are render paths,
 * where a throw is a blank screen (GOTCHAS §8). An unknown venue falls back to
 * the conservative answer: no series selector, no fan-out.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.usesSeriesSelector = usesSeriesSelector;
exports.labelsBothSides = labelsBothSides;
exports.tradingDisabledReason = tradingDisabledReason;
exports.sellDisabledReason = sellDisabledReason;
exports.canTradeVenue = canTradeVenue;
exports.isFanOutTarget = isFanOutTarget;
exports.supportsFullSell = supportsFullSell;
exports.marketOrder = marketOrder;
exports.orderbookIntervalMs = orderbookIntervalMs;
const registry_1 = require("./registry");
function capsOf(venue) {
    if (!venue || !(0, registry_1.hasVenue)(venue))
        return null;
    return (0, registry_1.getVenue)(venue).capabilities;
}
/**
 * Does this venue use Kalshi's prefix+suffix series selector (`KXNBA` + `GAME`),
 * as opposed to a flat league list?
 *
 * Gates the suffix selector, the custom prefix/suffix entries, the combined
 * category derivation and the favourite pair.
 */
function usesSeriesSelector(venue) {
    return capsOf(venue)?.leagueSelector === "series-prefix-suffix";
}
/**
 * Does the venue name BOTH sides of a market?
 *
 * False for Kalshi, whose binary contracts name only the YES side — the NO
 * label falls back to the literal "Opposing Team".
 */
function labelsBothSides(venue) {
    return capsOf(venue)?.sideLabels === "both";
}
/**
 * Why this venue cannot be traded, or null when it can.
 *
 * Lives on the adapter as a string rather than a boolean because the UI has to
 * SHOW the reason — so the reason and the flag are one fact
 * (ai/novig/phase-3-discovery.md §1.1). The venue's own order stubs throw this
 * same string, which is what keeps the banner, the tooltip and the error
 * message from drifting apart.
 *
 * An unknown venue reads as tradeable: this is a display gate, and the order
 * path has its own guards. Blocking on an id we failed to resolve would turn a
 * registry slip into a silently dead buy button.
 */
function tradingDisabledReason(venue) {
    if (!venue || !(0, registry_1.hasVenue)(venue))
        return null;
    return (0, registry_1.getVenue)(venue).tradingDisabledReason ?? null;
}
/** Why this venue can't sell right now (view-only, or buys only); null if it can. */
function sellDisabledReason(venue) {
    if (!venue || !(0, registry_1.hasVenue)(venue))
        return null;
    const adapter = (0, registry_1.getVenue)(venue);
    return adapter.tradingDisabledReason ?? adapter.sellDisabledReason ?? null;
}
/** Can the operator place an order on this venue at all? */
function canTradeVenue(venue) {
    return tradingDisabledReason(venue) === null;
}
/** May this venue be a fan-out target in the additional/parallel modals? */
function isFanOutTarget(venue) {
    return capsOf(venue)?.fanOutTarget === true;
}
/** Does the venue offer the "full" sell mode (liquidate the whole position)? */
function supportsFullSell(venue) {
    return capsOf(venue)?.sellFullPosition === true;
}
/** How the picker orders an event's markets; an unknown venue keeps the id-length order. */
function marketOrder(venue) {
    return capsOf(venue)?.marketOrder ?? "id-length";
}
/** Default orderbook poll interval for a venue, in ms. */
function orderbookIntervalMs(venue, fallback = 250) {
    return capsOf(venue)?.orderbookIntervalMs ?? fallback;
}
